// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Column,
  Dialog,
  DownloadIcon,
  Row,
  Select,
  Spinner,
  Text,
} from "@hightouch/htui";

import {
  ESP_DIALECTS,
  type EspDialect,
} from "@hightouch/lib/llm/lifecycle-studio/esp-templating/dialects";

import { useCompileContentEmailDynamicContentQuery } from "src/graphql";
import { useSavedDynamicSlots } from "src/pages/lifecycle-studio/emails/email-editor/canvas/dynamic-content/use-saved-dynamic-slots";
import { useEspTarget } from "src/pages/lifecycle-studio/emails/hooks/use-esp-target";
import { downloadText, sanitizeFilename } from "src/utils/download";

import {
  DESTINATION_TYPE_LABEL,
  ESP_DESTINATION_TYPES,
  getDestinationType,
  isTriggerSyncDestinationType,
  type DeliveryType,
  type DestinationType,
} from "./dynamic-content-deployment";

/** Display casing for the compiled dialect; the target → dialect mapping
 * itself comes from the backend's `ESP_DIALECTS` (the compile source of
 * truth), so only presentation strings live here. */
const DIALECT_DISPLAY_NAME: Record<EspDialect, string> = {
  handlebars: "Handlebars",
  liquid: "Liquid",
  django: "Django",
  ampscript: "AMPscript",
  zephyr: "Zephyr",
  twig: "Twig",
};

function dialectLabel(target: DestinationType): string | null {
  const capability = ESP_DIALECTS[target];
  return capability ? DIALECT_DISPLAY_NAME[capability.dialect] : null;
}

/** Multi-line alert body: htui's Alert renders a string message as one
 * paragraph, so per-item lines need explicit rows. */
const AlertMessageList = ({ items }: { items: string[] }) => (
  <Column gap={1}>
    {items.map((item) => (
      <Text key={item} size="sm">
        {item}
      </Text>
    ))}
  </Column>
);

type DownloadCompiledHtmlModalProps = {
  isOpen: boolean;
  onClose: () => void;
  emailId: string;
  /** Version to compile; null falls back to the email's current version. */
  versionId: string | null;
  emailName: string | null;
  /** Enables ESP resolution (campaign → brand → workspace default) to
   * preselect the format. */
  campaignId?: string;
  onDownloaded?: (properties: {
    compile_target: string;
    delivery_type: string;
  }) => void;
};

/**
 * Download a dynamic-content email as a deployable template: every branch
 * serialized into the target ESP's templating dialect (the same read-only
 * compile the deploy flow runs), instead of raw `data-ht-dynamic-slot`
 * markers no provider understands.
 *
 * The delivery type is auto-detected from the slots' condition mode rather
 * than picked in the modal, because each mode only compiles one way:
 * `esp_profile` conditions require the ESP-orchestrated compile (ESP-native
 * predicates the provider evaluates), while audience conditions require the
 * trigger-sync compile (`ht_br_*` per-recipient fields — esp_orchestrated
 * has no audience→predicate translation, and trigger_sync silently drops
 * esp-mode branches). AI Decisioning is never offered: its template paths
 * only resolve inside an AID flow.
 */
export const DownloadCompiledHtmlModal = ({
  isOpen,
  onClose,
  emailId,
  versionId,
  emailName,
  campaignId,
  onDownloaded,
}: DownloadCompiledHtmlModalProps) => {
  const [target, setTarget] = useState<DestinationType | null>(null);

  // Mixed-mode emails (some slots esp, some audience) compile as
  // esp_orchestrated: it errors loudly on the audience slots, where
  // trigger_sync would silently drop the esp branches' content.
  const { slots } = useSavedDynamicSlots(emailId);
  const deliveryType: DeliveryType = slots.some(
    (slot) => slot.assignmentSource === "esp_profile",
  )
    ? "esp_orchestrated"
    : "trigger_sync";

  const targetOptions = useMemo(
    () =>
      ESP_DESTINATION_TYPES.filter(
        (t) =>
          deliveryType !== "trigger_sync" || isTriggerSyncDestinationType(t),
      ).map((t) => {
        const dialect = dialectLabel(t);
        return {
          label: dialect
            ? `${DESTINATION_TYPE_LABEL[t]} (${dialect})`
            : DESTINATION_TYPE_LABEL[t],
          value: t,
        };
      }),
    [deliveryType],
  );

  // Preselect the format from the email's resolved ESP (campaign → brand →
  // workspace default) — the provider the email would actually deploy to.
  // Skipped when that ESP can't compile under the detected delivery type
  // (e.g. Klaviyo with audience conditions).
  const { destination: espDestination } = useEspTarget({
    campaignId,
    enabled: isOpen,
  });
  useEffect(() => {
    if (!isOpen || target !== null) return;
    const resolved = getDestinationType(espDestination?.type ?? "");
    if (resolved && targetOptions.some((option) => option.value === resolved)) {
      setTarget(resolved);
    }
  }, [isOpen, target, espDestination?.type, targetOptions]);

  // Same freshness rationale as the deploy modal: slot saves opt out of the
  // global mutation invalidation, so a cached compile could predate the
  // user's latest branch edits.
  const {
    data: compiled,
    isLoading: isCompiling,
    error: compileError,
  } = useCompileContentEmailDynamicContentQuery(
    {
      input: {
        emailId,
        versionId,
        target: target ?? "iterable",
        deliveryType,
      },
    },
    {
      enabled: isOpen && target !== null,
      staleTime: 0,
      refetchOnMount: "always",
      select: (data) => data.compileContentEmailDynamicContent,
    },
  );

  // Compile errors don't block the download: the compiler still emits a
  // clean template where errored slots stay static (baked content, marker
  // stripped — see compileDynamicContent). Deploys block on errors; a file
  // download can degrade with a visible warning instead.
  const canDownload = target !== null && !isCompiling && !!compiled;

  // Compiler messages name slots by uuid — meaningless in the UI. Map the
  // ids back to slot display names and summarize; the fix itself happens in
  // the Dynamic content tab, so the raw detail adds nothing here.
  const erroredSlotNames = useMemo(() => {
    if (!compiled || compiled.errors.length === 0) return [];
    return [
      ...new Set(
        slots
          .filter((slot) =>
            compiled.errors.some((error) => error.includes(slot.id)),
          )
          .map((slot) => slot.name),
      ),
    ];
  }, [compiled, slots]);

  const handleDownload = () => {
    if (!compiled || target === null) return;
    const filenameBase = sanitizeFilename(emailName ?? "") || "email";
    downloadText(compiled.template, `${filenameBase}-${target}.html`);
    onDownloaded?.({ compile_target: target, delivery_type: deliveryType });
    onClose();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Download HTML"
      variant="info"
      width="lg"
      actions={
        <Row gap={2} justify="flex-end">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={DownloadIcon}
            onClick={handleDownload}
            isDisabled={!canDownload}
          >
            Download
          </Button>
        </Row>
      }
    >
      <Column gap={5}>
        <Text color="text.secondary">
          This email has dynamic content, so the download compiles every variant
          into a single template in your provider&rsquo;s templating language.
        </Text>
        <Column gap={1}>
          <Text fontWeight="medium">Format</Text>
          <Select
            placeholder="Select a provider format"
            options={targetOptions}
            optionLabel={(option) => option.label}
            optionValue={(option) => option.value}
            value={target ?? undefined}
            onChange={(value) => setTarget(value ?? null)}
            width="100%"
          />
        </Column>
        {isCompiling && (
          <Row gap={2} align="center">
            <Spinner size="sm" />
            <Text size="sm" color="text.secondary">
              Compiling template…
            </Text>
          </Row>
        )}
        {Boolean(compileError) && (
          <Alert
            variant="inline"
            type="error"
            title="Failed to compile"
            message={
              compileError instanceof Error
                ? compileError.message
                : "The template could not be compiled. Try again."
            }
          />
        )}
        {compiled && compiled.errors.length > 0 && (
          <Alert
            variant="inline"
            type="warning"
            title="Some dynamic content won't compile"
            message={`${
              erroredSlotNames.length > 0
                ? erroredSlotNames.join(", ")
                : "Some slots"
            } will export as static content — the variant currently saved in the email. Fix the variant conditions in the Dynamic content tab to include every variant.`}
          />
        )}
        {compiled && compiled.warnings.length > 0 && (
          <Alert
            variant="inline"
            type="warning"
            title="Compiled with warnings"
            message={
              <AlertMessageList
                items={compiled.warnings.map((warning) =>
                  slots.reduce(
                    (message, slot) =>
                      message.replaceAll(slot.id, `"${slot.name}"`),
                    warning,
                  ),
                )}
              />
            }
          />
        )}
        {compiled &&
          deliveryType === "trigger_sync" &&
          compiled.requiredFields.length > 0 && (
            <Text size="sm" color="text.secondary">
              Sends must supply these per-recipient fields:{" "}
              {compiled.requiredFields.join(", ")}
            </Text>
          )}
      </Column>
    </Dialog>
  );
};
